import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { OBRecord } from '../../services/api/domains/products/types';
import { formatDateTime } from '../../utils/dateConversion';

// ---------------------------------------------------------------------------
// Config type — 13.1.1
// ---------------------------------------------------------------------------

export interface OBPanelFieldConfig {
  /** Must be a valid OBRecord key; used as React key. `compute` overrides the value lookup. */
  key: keyof OBRecord;
  label: string;
  /** When true the field is always visible; when false it appears inside the secondary accordion. */
  defaultVisible: boolean;
  /** Optional formatter applied to the raw field value. */
  format?: (val: unknown) => string;
  /**
   * Optional computed value function. When provided, overrides the normal `record[key]` lookup.
   * Used for fields derived from multiple OBRecord properties (e.g. Application = appl_type + appl_no).
   */
  compute?: (record: OBRecord) => string;
}

const OB_PANEL_FIELDS: OBPanelFieldConfig[] = [
  // 4 default-visible fields
  {
    key: 'trade_name',
    label: 'Trade Name',
    defaultVisible: true,
  },
  {
    key: 'appl_no',
    label: 'Application',
    defaultVisible: true,
    compute: (r: OBRecord) =>
      `${r.appl_type ?? ''}${r.appl_no ?? ''}`.trim() || '—',
  },
  {
    key: 'approval_date',
    label: 'Approval Date',
    defaultVisible: true,
    format: (val: unknown) => formatDateTime(val) || '—',
  },
  {
    key: 'te_code',
    label: 'TE Code',
    defaultVisible: true,
  },
  // 4 accordion-hidden fields
  {
    key: 'strength',
    label: 'Strength',
    defaultVisible: false,
  },
  {
    key: 'df',
    label: 'Dosage Form',
    defaultVisible: false,
  },
  {
    key: 'route',
    label: 'Route',
    defaultVisible: false,
  },
  {
    key: 'applicant_full_name',
    label: 'Applicant',
    defaultVisible: false,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveValue(field: OBPanelFieldConfig, record: OBRecord): string {
  if (field.compute) return field.compute(record);
  const raw = record[field.key];
  if (raw === null || raw === undefined || raw === '') return '—';
  if (field.format) return field.format(raw) || '—';
  return String(raw);
}

// ---------------------------------------------------------------------------
// Sub-component — a single read-only label/value row
// ---------------------------------------------------------------------------

const FieldRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div
    style={{
      display: 'flex',
      gap: '8px',
      padding: '4px 0',
      fontSize: '14px',
      lineHeight: '1.4',
    }}>
    <span
      style={{
        minWidth: '120px',
        fontWeight: 600,
        color: '#6b7280',
        flexShrink: 0,
      }}>
      {label}
    </span>
    <span style={{ color: '#111827', wordBreak: 'break-word' }}>{value}</span>
  </div>
);

// ---------------------------------------------------------------------------
// OrangeBookPanel — 13.1.2
// ---------------------------------------------------------------------------

interface OrangeBookPanelProps {
  obRecords: OBRecord[];
  isFdaLinked: boolean;
}

/**
 * Read-only FDA Orange Book section for the Product modal.
 * Renders nothing when is_fda_linked is false or ob_records is empty.
 * FDA is the authoritative source — no OB data is editable here.
 */
const OrangeBookPanel: React.FC<OrangeBookPanelProps> = ({
  obRecords,
  isFdaLinked,
}) => {
  const [secondaryExpanded, setSecondaryExpanded] = React.useState(false);

  if (!isFdaLinked || obRecords.length === 0) return null;

  const record = obRecords[0];

  const visibleFields = OB_PANEL_FIELDS.filter(f => f.defaultVisible);
  const hiddenFields = OB_PANEL_FIELDS.filter(f => !f.defaultVisible);

  return (
    <div
      style={{
        margin: '20px 0 8px 0',
        border: '1px solid #fed7aa',
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: '#fff7ed',
      }}>
      {/* Panel header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: '#ffedd5',
          borderBottom: '1px solid #fed7aa',
        }}>
        <span
          style={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#c2410c',
            backgroundColor: '#fdba74',
            padding: '2px 6px',
            borderRadius: '4px',
          }}>
          FDA
        </span>
        <span
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: '#9a3412',
          }}>
          Orange Book
        </span>
      </div>

      {/* Always-visible fields */}
      <div style={{ padding: '8px 12px 4px 12px' }}>
        {visibleFields.map(field => (
          <FieldRow
            key={field.key}
            label={field.label}
            value={resolveValue(field, record)}
          />
        ))}
      </div>

      {/* Secondary (accordion-hidden) fields */}
      <Accordion
        expanded={secondaryExpanded}
        onChange={() => setSecondaryExpanded(prev => !prev)}
        slotProps={{ transition: { unmountOnExit: false } }}
        disableGutters
        elevation={0}
        sx={{ '&:before': { display: 'none' } }}
        style={{ backgroundColor: 'transparent' }}>
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon style={{ color: '#c2410c', fontSize: '18px' }} />
          }
          style={{
            minHeight: '32px',
            padding: '0 12px',
            fontSize: '12px',
            fontWeight: 600,
            color: '#c2410c',
            backgroundColor: 'transparent',
          }}>
          More details
        </AccordionSummary>
        <AccordionDetails
          style={{
            padding: '0 12px 8px 12px',
            backgroundColor: 'transparent',
          }}>
          {hiddenFields.map(field => (
            <FieldRow
              key={field.key}
              label={field.label}
              value={resolveValue(field, record)}
            />
          ))}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default OrangeBookPanel;
